import React, { useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { throttle } from "throttle-debounce";
import { Tab } from "./Tab";
import { Icon } from "../../../components/Icons";
import { RCL } from "../../../components/RCL";
import * as styles from "./Tab.module.scss";

const TabList = ({
	data,
	selectedKey,
	onClick,
	onKeyDown,
	activeClassName,
	className,
	tabClassName,
	isFocused,
	focusedKey,
	tabsWrapperClassName,
	tabsRightButtonClassName,
	tabsLeftButtonClassName,
	tabIconClassName,
}) => {
	const ref = useRef(null);
	const [hideButtons, setHideButtons] = useState(true);
	const [disableLeftButton, setDisableLeftButton] = useState(true);
	const [disableRightButton, setDisableRightButton] = useState(true);
	const [init, setInit] = useState(true);

	const updateButtons = () => {
		const { scrollLeft, scrollWidth, clientWidth } = ref.current;
		if (scrollWidth > clientWidth) {
			setHideButtons(false);
		} else {
			setHideButtons(true);
		}
		if (scrollLeft === 0) {
			setDisableLeftButton(true);
		} else {
			setDisableLeftButton(false);
		}
		if (scrollLeft + clientWidth >= scrollWidth) {
			setDisableRightButton(true);
		} else {
			setDisableRightButton(false);
		}
	};
	function sideScroll(element, direction, speed, distance, step) {
		let scrollAmount = 0;
		const slideTimer = setInterval(() => {
			if (direction === "left") {
				// eslint-disable-next-line no-param-reassign
				element.scrollLeft -= step;
			} else {
				// eslint-disable-next-line no-param-reassign
				element.scrollLeft += step;
			}
			scrollAmount += step;
			updateButtons();
			if (scrollAmount >= distance) {
				window.clearInterval(slideTimer);
			}
		}, speed);
	}

	const updateActiveMenu = e => {
		const { clientWidth, offsetLeft } = ref.current;

		const isOverflowRight =
			offsetLeft +
			clientWidth -
			(e.currentTarget.getBoundingClientRect().x + e.currentTarget.getBoundingClientRect().width);

		const isOverflowLeft = e.currentTarget.getBoundingClientRect().x - offsetLeft;

		const isTabOverflow = clientWidth < e.currentTarget.getBoundingClientRect().width;

		// Determine if the active tab is:
		// 1) cut off on the left or
		// 2) scroll region is too short to display the full tab content
		if (isOverflowLeft < 0 || isTabOverflow) {
			if (isTabOverflow) {
				const adjustment = isOverflowLeft <= 0 ? Math.abs(isOverflowLeft) : -isOverflowLeft;
				// Jump to position on left
				sideScroll(ref.current, "left", 25, adjustment, adjustment);
			} else {
				// Animate to threshold
				sideScroll(ref.current, "left", 25, Math.abs(isOverflowLeft), 15);
			}
		} else if (isOverflowRight < 0) {
			// Animate to threshold
			sideScroll(ref.current, "right", 25, Math.abs(isOverflowRight), 15);
		}
	};

	const updateAutoScrolling = () => {
		ref.current.childNodes.forEach(listItem => {
			if (listItem.dataset.testid === selectedKey) {
				// Defer until DOM has loaded
				setTimeout(() => {
					listItem.dispatchEvent(new KeyboardEvent("keyup"));
					setInit(false);
				}, 250);
			}
		});
	};

	useEffect(() => {
		updateButtons();
		return () => {};
	}, [ref]);

	useEffect(() => {
		if (init && selectedKey) {
			// Initialize active tab scroll placement
			updateAutoScrolling();
		}

		return () => {};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [init, selectedKey]);

	useEffect(() => {
		const handleResize = throttle(200, () => {
			updateButtons();
			updateAutoScrolling();
		});

		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	});
	if (!data?.length) {
		return null;
	}
	return (
		<div
			className={cx(styles.tabsWrapper, {
				[tabsWrapperClassName]: tabsWrapperClassName,
			})}>
			{!hideButtons && (
				<button
					type="button"
					className={tabsLeftButtonClassName}
					disabled={disableLeftButton}
					aria-label={RCL({ searchKey: "navigate-left" })}
					onClick={() => {
						sideScroll(ref.current, "left", 25, 150, 10);
					}}>
					<Icon name="arrow-left" />
				</button>
			)}
			<div
				role="tablist"
				className={cx(styles.tabs, {
					[className]: className,
				})}
				ref={ref}
				onScroll={() => {
					updateButtons();
				}}>
				{data.map(tab => {
					// Standardize key
					// eslint-disable-next-line no-param-reassign
					tab.eventKey = `${tab.eventKey.trim().replace(/\s/g, "-").toLowerCase()}`;
					const id = tab.eventKey;

					return (
						<Tab
							key={id}
							id={id}
							label={tab.label}
							subLabel={tab.subLabel}
							newLabel={tab.newLabel}
							icon={tab.icon}
							selectedKey={selectedKey}
							onClick={(e, selected) => {
								onClick(e, selected);
								updateActiveMenu(e);
							}}
							onKeyDown={onKeyDown}
							onKeyUp={e => {
								updateActiveMenu(e);
							}}
							activeClassName={cx(activeClassName, styles.active)}
							className={cx(styles.tab, {
								[tabClassName]: tabClassName,
							})}
							focusedKey={focusedKey}
							isFocused={isFocused}
							tabIconClassName={tabIconClassName}
						/>
					);
				})}
			</div>
			{!hideButtons && (
				<button
					type="button"
					disabled={disableRightButton}
					className={tabsRightButtonClassName}
					aria-label={RCL({ searchKey: "navigate-right" })}
					onClick={() => {
						sideScroll(ref.current, "right", 25, 150, 10);
					}}>
					<Icon name="arrow-right" />
				</button>
			)}
		</div>
	);
};

TabList.propTypes = {
	data: PropTypes.arrayOf(PropTypes.object).isRequired,
	selectedKey: PropTypes.string,
	onClick: PropTypes.func.isRequired,
	onKeyDown: PropTypes.func.isRequired,
	activeClassName: PropTypes.string.isRequired,
	className: PropTypes.string.isRequired,
	tabClassName: PropTypes.string.isRequired,
	isFocused: PropTypes.bool.isRequired,
	focusedKey: PropTypes.string,
	tabIconClassName: PropTypes.string,
	tabsWrapperClassName: PropTypes.string,
	tabsRightButtonClassName: PropTypes.string,
	tabsLeftButtonClassName: PropTypes.string,
};

TabList.defaultProps = {
	selectedKey: undefined,

	tabsWrapperClassName: "",
	tabsRightButtonClassName: "",
	tabsLeftButtonClassName: "",
	focusedKey: null,
	tabIconClassName: "",
};

export default TabList;
export { TabList };
